<template>
    <div>
        <div class="flex flex-col gap-3 mb-4">
            <DayStrip :days="dayEntries" @select="selectDay" />

            <div class="flex items-center justify-between gap-4 flex-wrap">
                <div
                    v-if="isOpen"
                    class="shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full"
                    :class="currentDayOverride ? 'bg-amber-50 text-amber-900' : 'bg-gray-200 text-gray-700'"
                >
                    <i
                        class="fa-solid fa-clock text-xs"
                        :class="currentDayOverride ? 'text-amber-700' : 'text-gray-500'"
                        aria-hidden="true"
                    />
                    <span class="text-sm font-semibold">{{ openingLabel(defineHours) }}</span>
                </div>
                <div v-else />

                <div class="flex gap-3 items-center flex-wrap justify-end">
                    <select
                        class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-primary-500 focus:border-primary-500 block w-max p-2.5 pr-8"
                        v-model="screenWidthViewSingleSpace"
                        aria-label="Show a single table"
                    >
                        <option :value="-1">All tables</option>
                        <option v-for="(space, index) in spaces" :value="index">{{ space.name }}</option>
                    </select>

                    <div
                        v-if="hasHiddenTables"
                        class="flex items-center gap-1 p-1 bg-white border border-gray-300 rounded-lg"
                    >
                        <button
                            type="button"
                            class="flex items-center justify-center w-8 h-8 rounded-md text-gray-700 transition enabled:hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                            :disabled="!canPageBack"
                            aria-label="Show earlier tables"
                            @click="changeStartingSpace(-1)"
                        >
                            <i class="fa-solid fa-chevron-left text-xs" aria-hidden="true" />
                        </button>
                        <span class="px-1 text-sm font-semibold text-gray-700 whitespace-nowrap">
                            Tables {{ firstShown }}–{{ lastShown }} of {{ totalTables }}
                        </span>
                        <button
                            type="button"
                            class="flex items-center justify-center w-8 h-8 rounded-md text-gray-700 transition enabled:hover:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                            :disabled="!canPageForward"
                            aria-label="Show later tables"
                            @click="changeStartingSpace(1)"
                        >
                            <i class="fa-solid fa-chevron-right text-xs" aria-hidden="true" />
                        </button>
                    </div>

                    <div
                        v-else-if="isFiltered"
                        class="flex items-center gap-2 py-1 pl-3 pr-1 bg-white border border-gray-300 rounded-lg"
                    >
                        <span class="text-sm font-semibold text-gray-700 whitespace-nowrap">
                            1 of {{ totalTables }} tables
                        </span>
                        <button
                            type="button"
                            class="h-8 px-2 rounded-md text-sm font-semibold text-primary-800 transition hover:bg-primary-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                            @click="screenWidthViewSingleSpace = -1"
                        >
                            Show all
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div
            v-if="isOpen && currentDayOverride"
            class="flex items-start gap-2 mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg"
        >
            <i class="fa-solid fa-circle-info text-amber-700 mt-0.5" aria-hidden="true" />
            <div>
                <div class="text-sm font-bold text-amber-900">{{ specialHoursHeading(currentDay) }}</div>
                <div v-if="currentDayOverride.reason" class="text-sm text-amber-900">
                    {{ currentDayOverride.reason }}
                </div>
            </div>
        </div>

        <div v-if="!isOpen" class="mb-4 p-8 bg-white border border-gray-300 rounded-2xl text-center">
            <i class="fa-solid fa-lock text-gray-400 text-3xl" aria-hidden="true" />
            <h3 class="mt-3 text-lg font-bold text-gray-900">We're closed on {{ closedDayLabel }}</h3>
            <p class="mt-1 text-base text-gray-600">
                {{ currentDayOverride?.reason || "We're not taking bookings on this day." }}
            </p>
            <button
                v-if="nextOpenDay"
                type="button"
                class="mt-4 inline-flex items-center justify-center h-12 px-5 rounded-lg bg-primary text-sm font-bold uppercase tracking-widest text-white transition hover:bg-tertiary hover:text-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                @click="selectDay(nextOpenDay)"
            >
                Jump to {{ nextOpenDay.toFormat("ccc d") }}
            </button>
        </div>

        <div
            v-if="isOpen && !visibleHours.length"
            class="mb-4 p-8 bg-white border border-gray-300 rounded-2xl text-center"
        >
            <i class="fa-solid fa-moon text-gray-400 text-3xl" aria-hidden="true" />
            <h3 class="mt-3 text-lg font-bold text-gray-900">That's today done</h3>
            <p class="mt-1 text-base text-gray-600">
                We closed at {{ closingLabel }}. Pick another day to book a table.
            </p>
            <button
                v-if="nextOpenDay"
                type="button"
                class="mt-4 inline-flex items-center justify-center h-12 px-5 rounded-lg bg-primary text-sm font-bold uppercase tracking-widest text-white transition hover:bg-tertiary hover:text-black focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                @click="selectDay(nextOpenDay)"
            >
                Jump to {{ nextOpenDay.toFormat("ccc d") }}
            </button>
        </div>

        <div v-if="isOpen && visibleHours.length" class="calendar w-full" :style="gridTracks">
            <div />
            <div class="calendar-cell w-[6rem] p-4" v-for="hour in visibleHours">
                <div class="font-semibold">{{ hour.visual }}</div>
            </div>
            <template v-for="entry in columnSchedules">
                <div class="calendar-cell flex-col justify-center px-4 py-3">
                    <button
                        type="button"
                        class="block w-full text-sm font-semibold text-left truncate rounded transition hover:text-primary-800 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
                        :aria-label="`Table details for ${entry.column.space.name}`"
                        @click="$emits('viewSpace', entry.column.space)"
                    >
                        {{ entry.column.space.name }}
                    </button>
                    <div class="text-xs font-semibold text-gray-700">
                        <span v-if="entry.price">{{ entry.price }} · </span>
                        <span :class="entry.free === 0 ? 'text-secondary-800' : 'text-gray-700'">
                            {{ entry.free === 0 ? "Full" : "Spaces available" }}
                        </span>
                    </div>
                </div>
                <div
                    v-for="cell in entry.cells"
                    class="calendar-cell p-1"
                    :style="{ 'grid-row': 'span ' + cell.length }"
                >
                    <SlotBlock
                        :cell="cell"
                        :space-name="entry.column.space.name"
                        density="compact"
                        @book="$emits('purchaseBlock', null, cell.hour, entry.column.space)"
                        @view-event="viewEvent"
                    />
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
    import { computed, ref, watch } from "vue";
    import { useWindowSize } from "@vueuse/core";
    import PrimaryButton from "../InertiaComponents/PrimaryButton.vue";
    import DayStrip from "./Calendar/DayStrip.vue";
    import SlotBlock from "./Calendar/SlotBlock.vue";
    import { getDateFromISO } from "../Helpers/DateHelper.js";
    import { router } from "@inertiajs/vue3";
    import {
        buildDaySchedule,
        cellsFromHour,
        dayEntriesFor,
        firstUnfinishedHourIndex,
        formatHourLong,
        isBookable,
        nextOpenDayAfter,
        openingLabel,
        priceHintFor,
        specialHoursHeading,
    } from "../Composables/useBookingSchedule.js";

    const props = defineProps({
        defineHours: Object,
        currentDay: Object,
        computedCalendarView: Object,
        spaces: Object,
        events: Object,
        currentDayOverride: Object,
        openingTimes: Object,
        openingTimeOverrides: Object,
    });

    const $emits = defineEmits(["changeCurrentDay", "purchaseBlock", "viewSpace"]);

    const isOpen = computed(() => !!props.defineHours && props.defineHours.length > 0);

    const dayEntries = computed(() =>
        dayEntriesFor({
            currentDay: props.currentDay,
            calendarView: props.computedCalendarView,
            events: props.events,
            openingTimes: props.openingTimes,
            overrides: props.openingTimeOverrides,
        }),
    );

    const nextOpenDay = computed(() =>
        nextOpenDayAfter(props.currentDay, props.openingTimes, props.openingTimeOverrides),
    );

    const closedDayLabel = computed(() => props.currentDay.toFormat("cccc d"));

    /**
     * The hour rows the grid draws. On today the hours that have finished come off the top: a
     * past cell here is deliberately blank, so a whole morning of them read as a stack of empty
     * rows sitting above the first hour anyone could actually book.
     */
    const visibleHours = computed(() =>
        (props.defineHours || []).slice(firstUnfinishedHourIndex(props.currentDay, props.defineHours)),
    );

    /** Only ever shown once every hour has gone, to say why there is no grid. */
    const closingLabel = computed(() =>
        isOpen.value ? formatHourLong(props.defineHours[props.defineHours.length - 1].time + 1) : "",
    );

    const selectDay = (day) => {
        if (!day.hasSame(props.currentDay, "day")) {
            $emits("changeCurrentDay", day);
        }
    };

    //
    // HOW MANY TABLES FIT
    //

    const screenWidthViewSingleSpace = ref(-1);
    const screenWidthSelectedStartSpace = ref(0);

    // useWindowSize keeps this reactive without a hand-rolled resize listener. The old one
    // reset the chosen table and the scroll offset on every resize event, so rotating a
    // device or opening a keyboard threw away what you were looking at.
    const { width: screenWidth } = useWindowSize();

    /** Column count by viewport width. Widths below the smallest tier get the mobile view. */
    const columnsForWidth = (width) => {
        if (width <= 849) return 2;
        if (width <= 1099) return 3;
        if (width <= 1400) return 4;
        if (width <= 1700) return 5;
        return 6;
    };

    watch(screenWidthViewSingleSpace, (e) => {
        if (e === -1) {
            screenWidthSelectedStartSpace.value = 0;
        }
    });

    const getSpacesInView = computed(() => {
        if (screenWidthViewSingleSpace.value > -1 && props.computedCalendarView[screenWidthViewSingleSpace.value]) {
            return [props.computedCalendarView[screenWidthViewSingleSpace.value]];
        }

        return props.computedCalendarView.slice(
            screenWidthSelectedStartSpace.value,
            columnsForWidth(screenWidth.value) + screenWidthSelectedStartSpace.value,
        );
    });

    const changeStartingSpace = (amount) => {
        const tempAmount = screenWidthSelectedStartSpace.value + amount;

        if (tempAmount >= 0 && tempAmount <= totalTables.value - getSpacesInView.value.length) {
            screenWidthSelectedStartSpace.value = tempAmount;
        }
    };

    /**
     * How many tables there are versus how many fit. The grid used to page silently: the chevrons
     * simply stopped working at the ends, and nothing anywhere said a seventh table existed.
     */
    const totalTables = computed(() => props.computedCalendarView?.length || 0);

    const isFiltered = computed(() => screenWidthViewSingleSpace.value > -1);

    const hasHiddenTables = computed(() => !isFiltered.value && getSpacesInView.value.length < totalTables.value);

    const firstShown = computed(() => screenWidthSelectedStartSpace.value + 1);

    const lastShown = computed(() => screenWidthSelectedStartSpace.value + getSpacesInView.value.length);

    const canPageBack = computed(() => screenWidthSelectedStartSpace.value > 0);

    const canPageForward = computed(() => lastShown.value < totalTables.value);

    // Widening the window fits more columns, which can leave the offset pointing past the last
    // table — the count would then claim a range the grid is not showing.
    watch([() => columnsForWidth(screenWidth.value), totalTables], ([columns, total]) => {
        screenWidthSelectedStartSpace.value = Math.max(
            0,
            Math.min(screenWidthSelectedStartSpace.value, total - columns),
        );
    });

    /**
     * Every column's day, built once. The template used to call this per cell, so a hover class or
     * a resize tick rebuilt all six columns; the free count the header shows comes off the same
     * pass rather than walking the day a second time.
     */
    const columnSchedules = computed(() =>
        getSpacesInView.value.map((column) => {
            const cells = buildDaySchedule({
                day: props.currentDay,
                spaceView: column,
                events: props.events,
                hours: props.defineHours,
            });

            return {
                column: column,
                // Trimmed to the rows still on screen. A block that started this morning keeps
                // whatever of it is left rather than vanishing and freeing its own hours.
                cells: cellsFromHour(cells, visibleHours.value[0]?.time ?? 0),
                free: cells.filter(isBookable).length,
                price: priceHintFor(column.space),
            };
        }),
    );

    /**
     * One track per column we actually drew. This used to be repeat(auto-fit, ...) in the
     * stylesheet, which decided the track count from the container width while columnsForWidth
     * decided the item count from the viewport — so picking a single table left auto-fit
     * collapsing the empty tracks and handing that one column the entire width.
     */
    const gridTracks = computed(() => ({
        "grid-template-rows": `min-content repeat(${visibleHours.value.length}, 1fr)`,
        "grid-template-columns": `6rem repeat(${columnSchedules.value.length}, minmax(13rem, 24rem))`,
    }));

    // Only the event branch of a slot emits this, and only when it has an event to open — the
    // same guard MobileCalendar uses, so both views land on the same page from the same click.
    const viewEvent = (cell) => {
        if (cell.eventId) {
            router.get("/event/" + cell.eventId + "/" + getDateFromISO(cell.startsAt.toISO()).toSQL());
        }
    };
</script>

<style scoped>
    .calendar {
        display: grid;
        grid-auto-flow: column;
        /* Tracks come from gridTracks so the count matches the columns we actually rendered. */
        justify-content: start;
    }

    .calendar-cell {
        display: flex;
    }
</style>
